import { getAcompanhamento, createAcompanhamento, updateAcompanhamento, deleteAcompanhamento } from "@spark/services/acompanhamentos";
import { useState, useCallback } from 'react';
import { notification } from '@spark/ui-components/utils/notification';
/**
 * Hook compartilhado para operações CRUD de acompanhamento
 * @param {string} id - ID do acompanhamento
 * @returns {object} - Estados e funções para operações CRUD
 */

export const useAcompanhamento = (id = null) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null); // Carregar acompanhamento por ID

  const loadAcompanhamento = useCallback(async (acompanhamentoId = id) => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Carregando acompanhamento:', acompanhamentoId);
      const response = await getAcompanhamento(acompanhamentoId);
      const acompanhamento = response.data || response;
      console.log('📥 Dados carregados:', acompanhamento);
      setData(acompanhamento);
      return acompanhamento;
    } catch (err) {
      console.error('❌ Erro ao carregar acompanhamento:', err);
      setError(err);
      notification.error('Erro ao carregar acompanhamento');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]); // Criar novo acompanhamento

  const createAcompanhamentoData = useCallback(async payload => {
    try {
      setLoading(true);
      setError(null);
      console.log('✨ Criando acompanhamento:', payload);
      const response = await createAcompanhamento(payload);
      console.log('✅ Acompanhamento criado:', response);
      notification.success('Acompanhamento criado com sucesso!');
      return response;
    } catch (err) {
      console.error('❌ Erro ao criar acompanhamento:', err);
      setError(err);
      notification.error('Erro ao criar acompanhamento');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []); // Atualizar acompanhamento

  const updateAcompanhamentoData = useCallback(async (acompanhamentoId = id, payload) => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('📝 Atualizando acompanhamento:', acompanhamentoId, payload);
      const response = await updateAcompanhamento(acompanhamentoId, payload);
      console.log('✅ Acompanhamento atualizado:', response);
      notification.success('Acompanhamento atualizado com sucesso!'); // Recarregar dados atualizados

      if (data) {
        await loadAcompanhamento(acompanhamentoId);
      }

      return response;
    } catch (err) {
      console.error('❌ Erro ao atualizar acompanhamento:', err);
      setError(err);
      notification.error('Erro ao atualizar acompanhamento');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id, data, loadAcompanhamento]); // Deletar acompanhamento

  const deleteAcompanhamentoData = useCallback(async (acompanhamentoId = id) => {
    if (!acompanhamentoId) return;

    try {
      setLoading(true);
      setError(null);
      console.log('🗑️ Deletando acompanhamento:', acompanhamentoId);
      const response = await deleteAcompanhamento(acompanhamentoId);
      console.log('✅ Acompanhamento deletado:', response);
      notification.success('Acompanhamento excluído com sucesso!');
      setData(null);
      return response;
    } catch (err) {
      console.error('❌ Erro ao deletar acompanhamento:', err);
      setError(err);
      notification.error('Erro ao excluir acompanhamento');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]); // Reset dos estados

  const resetState = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);
  return {
    // Estados
    data,
    loading,
    error,
    // Ações
    loadAcompanhamento,
    createAcompanhamento: createAcompanhamentoData,
    updateAcompanhamento: updateAcompanhamentoData,
    deleteAcompanhamento: deleteAcompanhamentoData,
    resetState
  };
};
